import React, { useMemo, useState } from "react";
import { CreditCard, ChevronLeft, ChevronRight, Wallet, Trash2, Pencil } from "lucide-react";
import { detectIssuer } from "@/lib/issuer";

const currencyFormat = (value, currency = "GBP") => {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(n);
  } catch (e) {
    return `${currency} ${n.toFixed(2)}`;
  }
};

const StackedCard = ({ card, index, active, count, onSelect, homeCurrency = "GBP", fxRates = null }) => {
  const issuer = detectIssuer(card.name, card.provider_name);
  const isActive = active === index;
  const depth = index - active; // 0 = active (centered), negative = behind-left, positive = ahead-right
  const abs = Math.abs(depth);
  const offset = depth * 46;
  const scale = isActive ? 1 : Math.max(0.86, 1 - abs * 0.06);
  const opacity = abs > 3 ? 0 : Math.max(0.32, 1 - abs * 0.18);
  const rotate = depth * -1.8;
  const z = 100 - abs;

  const cardCcy = (card.currency || homeCurrency).toUpperCase();
  const home = (homeCurrency || "GBP").toUpperCase();
  const rate = fxRates && cardCcy !== home ? fxRates[cardCcy] : null;
  const balanceHome = rate && card.balance != null ? Number(card.balance) * rate : null;
  const limitHome = rate && card.credit_limit != null ? Number(card.credit_limit) * rate : null;

  return (
    <div
      className="absolute rounded-3xl overflow-hidden text-left"
      style={{
        width: "min(400px, 88%)",
        height: 240,
        left: "50%",
        top: 0,
        marginInline: 0,
        transform: `translate(calc(-50% + ${offset}px), 0) rotate(${rotate}deg) scale(${scale})`,
        opacity,
        zIndex: z,
        background: issuer.gradient,
        boxShadow: isActive
          ? "0 30px 60px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)"
          : "0 20px 40px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
        transition: "transform 0.5s cubic-bezier(0.2,0.65,0.3,1), opacity 0.4s ease, box-shadow 0.4s ease",
        pointerEvents: "none",
      }}
    >
      {/* Diagonal gloss */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(120deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0.06) 90%)",
        }}
      />
      {/* Grain */}
      <div
        className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-[0.10]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      <div className="relative h-full p-6 flex flex-col justify-between text-white pointer-events-none">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div
              className="text-[10px] uppercase tracking-[0.28em] font-semibold opacity-80"
              style={{ color: issuer.accent }}
            >
              {issuer.label}
            </div>
            <div
              className="mt-2 text-lg font-semibold tracking-tight truncate max-w-[220px]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {card.nickname || card.name}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {card.source === "manual" ? (
              <span className="text-[9px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/10 backdrop-blur border border-white/15">
                Manual
              </span>
            ) : (
              <span className="text-[9px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/10 backdrop-blur border border-white/15">
                Plaid
              </span>
            )}
          </div>
        </div>

        {/* Chip */}
        <div
          className="mt-2 w-11 h-8 rounded-md relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #F4C542 0%, #B78A17 100%)",
            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.25)",
          }}
          aria-hidden="true"
        >
          <div
            className="absolute inset-1 rounded-sm"
            style={{
              backgroundImage:
                "linear-gradient(#00000022 1px, transparent 1px), linear-gradient(90deg, #00000022 1px, transparent 1px)",
              backgroundSize: "8px 8px",
            }}
          />
        </div>

        {/* Number + footer */}
        <div>
          <div
            className="text-lg tracking-[0.35em] font-semibold"
            style={{ fontFamily: "'Manrope', monospace", letterSpacing: "0.35em" }}
          >
            ••••{" "}
            <span className="opacity-90" data-testid={`vault-lastfour-${card.id}`}>
              {card.last_four || "••••"}
            </span>
          </div>
          <div className="mt-3 flex items-end justify-between gap-4">
            <div>
              <div className="text-[9px] uppercase tracking-widest opacity-70">Balance</div>
              <div
                className="text-base font-semibold"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {currencyFormat(card.balance, card.currency)}
              </div>
              {balanceHome != null && (
                <div className="text-[10px] mt-0.5 opacity-70" data-testid={`vault-balance-home-${card.id}`}>
                  ≈ {currencyFormat(balanceHome, home)}
                </div>
              )}
            </div>
            {card.credit_limit && (
              <div className="text-right">
                <div className="text-[9px] uppercase tracking-widest opacity-70">Limit</div>
                <div
                  className="text-base font-semibold"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {currencyFormat(card.credit_limit, card.currency)}
                </div>
                {limitHome != null && (
                  <div className="text-[10px] mt-0.5 opacity-70" data-testid={`vault-limit-home-${card.id}`}>
                    ≈ {currencyFormat(limitHome, home)}
                  </div>
                )}
              </div>
            )}
          </div>
          {card.utilization !== null && card.utilization !== undefined && (
            <div className="mt-3 h-1 rounded-full overflow-hidden bg-white/15">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, card.utilization)}%`,
                  background: issuer.accent,
                  boxShadow: `0 0 10px ${issuer.accent}66`,
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CardVault = ({ cards = [], onRemove, onEdit, readOnly = false, homeCurrency = "GBP", fxRates = null }) => {
  const [active, setActive] = useState(0);
  const [confirmId, setConfirmId] = useState(null);

  const list = useMemo(() => cards.filter(Boolean), [cards]);
  const clampedActive = list.length ? Math.min(active, list.length - 1) : 0;

  const activeCard = list[clampedActive];
  const total = list.length;

  if (!total) {
    return (
      <section
        className="relative rounded-2xl border border-dashed border-[#1A2849] bg-[#0E172C]/40 p-10 text-center"
        data-testid="card-vault"
      >
        <div className="mx-auto w-14 h-14 rounded-2xl bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center" data-testid="vault-empty">
          <Wallet size={22} className="text-[#00E676]" />
        </div>
        <h3
          className="mt-5 text-white text-lg font-semibold tracking-tight"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Your Card Vault is empty
        </h3>
        <p className="mt-2 text-sm text-slate-300 max-w-md mx-auto">
          Link a credit card via Plaid or add one manually — each card shows up here with its issuer color and last four digits.
        </p>
      </section>
    );
  }

  return (
    <section
      className="relative rounded-2xl border border-[#1A2849] bg-[#0E172C] p-6 lg:p-8 overflow-hidden"
      data-testid="card-vault"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-300 font-semibold">
            Card Vault
          </div>
          <h2
            className="mt-2 text-xl font-semibold text-white tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Every card, stacked and ready
          </h2>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-xs text-slate-300">
          <span data-testid="vault-count">{total} card{total === 1 ? "" : "s"}</span>
          <span className="w-px h-4 bg-white/10" />
          <span>
            {clampedActive + 1} / {total}
          </span>
        </div>
      </div>

      <div className="relative mx-auto mt-2" style={{ height: 260 }}>
        {list.map((c, i) => (
          <StackedCard
            key={c.id}
            card={c}
            index={i}
            active={clampedActive}
            count={total}
            onSelect={setActive}
            homeCurrency={homeCurrency}
            fxRates={fxRates}
          />
        ))}
        {/* Hit zones — pointer targets sit above the visuals so click resolution is
            deterministic regardless of stacking / DOM order. Each background card gets a
            zone wide enough to cover its full exposed sliver, so users can tap anywhere on
            the visible part of a background card and have it come forward. */}
        {list.map((c, i) => {
          const depth = i - clampedActive;
          const isActiveZone = depth === 0;
          const zoneOffset = depth * 46;
          const stripW = 90;
          return (
            <button
              key={`hit-${c.id}`}
              type="button"
              onClick={() => setActive(i)}
              data-testid={`vault-card-${c.id}`}
              aria-label={`Card ${i + 1} of ${total}: ${c.nickname || c.name}`}
              className="absolute focus:outline-none focus:ring-2 focus:ring-[#00E676]/60 rounded-3xl"
              style={{
                left: "50%",
                top: "50%",
                width: isActiveZone ? "min(400px, 88%)" : stripW,
                height: 220,
                transform: `translate(calc(-50% + ${zoneOffset}px), -50%)`,
                background: "transparent",
                border: "none",
                cursor: isActiveZone ? "default" : "pointer",
                pointerEvents: isActiveZone ? "none" : "auto",
                zIndex: 210 - Math.abs(depth),
              }}
            />
          );
        })}
      </div>

      <div className="mt-6 mx-auto flex items-center justify-between gap-3" style={{ width: "min(400px, 88%)" }}>
        <button
          type="button"
          onClick={() => setActive((a) => Math.max(0, a - 1))}
          disabled={clampedActive === 0}
          data-testid="vault-prev"
          className="inline-flex items-center gap-1 rounded-full border border-white/10 hover:border-white/30 px-3 py-2 text-xs text-slate-300 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={13} /> Prev
        </button>
        <div className="flex items-center gap-1.5" data-testid="vault-dots">
          {list.map((c, i) => (
            <button
              key={c.id}
              onClick={() => setActive(i)}
              aria-label={`Card ${i + 1}`}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === clampedActive ? 20 : 6,
                background: i === clampedActive ? "#00E676" : "rgba(255,255,255,0.18)",
              }}
              data-testid={`vault-dot-${i}`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => setActive((a) => Math.min(total - 1, a + 1))}
          disabled={clampedActive >= total - 1}
          data-testid="vault-next"
          className="inline-flex items-center gap-1 rounded-full border border-white/10 hover:border-white/30 px-3 py-2 text-xs text-slate-300 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Next <ChevronRight size={13} />
        </button>
      </div>

      {activeCard && (
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-xs text-slate-300 flex items-center gap-2" data-testid="vault-active-hint">
            <CreditCard size={13} /> Tap any card to bring it forward
          </div>
          {!readOnly && onRemove && (
            confirmId === activeCard.id ? (
              <div className="flex items-center gap-2" data-testid="vault-remove-confirm">
                <span className="text-xs text-slate-300">Remove {activeCard.nickname || activeCard.name}?</span>
                <button
                  type="button"
                  onClick={() => {
                    onRemove(activeCard);
                    setConfirmId(null);
                  }}
                  data-testid="vault-remove-confirm-yes"
                  className="rounded-full bg-red-500/90 hover:bg-red-500 text-white font-semibold px-3 py-1.5 text-[11px] transition-colors"
                >
                  Yes, remove
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmId(null)}
                  data-testid="vault-remove-confirm-cancel"
                  className="rounded-full border border-white/10 hover:border-white/30 text-slate-300 hover:text-white px-3 py-1.5 text-[11px] transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 self-start sm:self-auto">
                {onEdit && activeCard.source === "manual" && (
                  <button
                    type="button"
                    onClick={() => onEdit(activeCard)}
                    data-testid={`vault-edit-${activeCard.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#00E676]/40 hover:border-[#00E676] bg-[#00E676]/[0.08] hover:bg-[#00E676]/[0.16] text-[#00E676] hover:text-white px-3 py-1.5 text-[11px] font-semibold transition-colors"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setConfirmId(activeCard.id)}
                  data-testid={`vault-remove-${activeCard.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 hover:border-red-500/60 bg-red-500/[0.06] hover:bg-red-500/[0.12] text-red-300 hover:text-red-200 px-3 py-1.5 text-[11px] font-semibold transition-colors"
                >
                  <Trash2 size={12} /> Remove card
                </button>
              </div>
            )
          )}
        </div>
      )}
    </section>
  );
};

export default CardVault;
